import {observer} from "mobx-react";
import {useContext, useEffect, useRef} from "react";
import {Button, Dialog, DialogContent, IconButton} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import {Frame} from "../components/common/Frame";
import {Header} from "../components/sections/Header";
import {AppRoutes} from "../AppRoutes";
import {useParams} from "react-router";
import {AppContext} from "../stores/AppStore";
import {RecordingStore, RecordingStoreContext} from "../stores/RecordingStore";
import useInstance from "../hooks/useInstance";
import {RecordingAnnotationsSidebar} from "../components/recordings/RecordingAnnotationsSidebar";
import {RecordingContentPanel} from "../components/recordings/RecordingContentPanel";
import {RecordingMetaPanel} from "../components/recordings/RecordingMetaPanel";
import {D} from "../Constants";
import {useMobx} from "../hooks/useMobx";

interface RecordingPageProps {
  scope: "my" | "organization";
}

export const RecordingPage = observer(({scope}: RecordingPageProps) => {
  const {id} = useParams<{id?: string}>();
  const app = useContext(AppContext)!;
  const store = useInstance(() => new RecordingStore(app, id ?? ""), [app, id]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const localState = useMobx(() => ({
    infoDialogOpen: false,
  }));

  useEffect(() => {
    if (!store) {
      return;
    }
    if (!store.annotations.length) {
      store.selectedAnnotation = undefined;
      store.editingAnnotation = undefined;
      return;
    }
    // Clear selection if the selected annotation no longer exists
    if (store.selectedAnnotation && !store.annotations.includes(store.selectedAnnotation)) {
      store.selectedAnnotation = undefined;
    }
  }, [store, store?.annotations.length]);

  if (!id || !store) return null;

  const recordingLabel = store.recording?.recording?.title ?? "...";
  const scopeCrumb: [string, string] = scope === "my"
    ? [AppRoutes.mySessions, "My Sessions"]
    : [AppRoutes.organizationSessions, "Organization Sessions"];

  return (
    <RecordingStoreContext.Provider value={store}>
      <Frame height={'100%'} direction="column">
        <Header
          pathCrumbs={[
            [AppRoutes.index, "Home"],
            scopeCrumb,
            [undefined, recordingLabel],
          ]}
        />

        <Frame row grow alignItems="stretch" style={{overflow: 'hidden'}}>
          <RecordingAnnotationsSidebar videoRef={videoRef}/>
          <RecordingContentPanel
            videoRef={videoRef}
            onOpenInfo={() => localState.infoDialogOpen = true}
          />
        </Frame>

        {/* Full session info dialog */}
        <Dialog
          open={localState.infoDialogOpen}
          onClose={() => localState.infoDialogOpen = false}
          maxWidth="md"
          fullWidth
          slotProps={{
            paper: {
              style: {
                borderRadius: D.radius(2),
                height: 'calc(100vh - 40px)',
              }
            }
          }}
        >
          <DialogContent style={{padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <RecordingMetaPanel dialog onClose={() => localState.infoDialogOpen = false} />
          </DialogContent>
        </Dialog>
      </Frame>
    </RecordingStoreContext.Provider>
  );
});
